/* src/components/layout/MainHeader.jsx — FULL component, copy-and-paste over the old one */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch }    from 'react-redux';
import { Helmet }                      from 'react-helmet';

import CurrencyCodeAdd from '@/components/common/CurrencyCodeAdd';
import { setTableSideDrawer } from '@/store/actions';
import { setCredit } from '@/store/creditSlice';
import { getFileUrl }         from '@/services/CommonService';

const MainHeader = ({ title, faviconIcon }) => {
  /* ─────────────────────────── STATE / REFS ─────────────────────────── */
  const { generalSetting, tableOrder, tableSideDrawer } = useSelector(
    (state) => state.GeneralSetting
  );
  const { enabled: creditEnabled, balance: creditBalance } = useSelector(
    (state) => state.Credit
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();

  /* On first mount, only for debugging: */
  useEffect(() => {
    console.log('[MainHeader] Initial jwt_token:', localStorage.getItem('jwt_token'));
    console.log('[MainHeader] Initial adminToken:', localStorage.getItem('adminToken'));
  }, []);

  /* Track login state based on token in localStorage. */
 const isCustomerLoggedIn = useSelector(
   (s) => !!(s.Auth?.userData?._id) ||
          !!localStorage.getItem('customer_jwt') ||
          !!localStorage.getItem('customerToken')
);
  /* Helper: remember where we are, to redirect back after login. */
  const getCurrentRoutePath = () =>
    window.location.pathname + window.location.search;

  /* ───────────────────────────  HANDLERS  ─────────────────────────── */
  const handleLogout = () => {
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customer_jwt');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('jwt_token');
    // wipe stale credit from Redux (and persisted store if you use redux-persist)
    dispatch(setCredit({ enabled: false, balance: 0, limit: 0 }));
    window.location.href = `/menu/table/${slug || ''}`;
  };

  const handleDrawer = () => {
    const newState = !tableSideDrawer;
    dispatch(setTableSideDrawer(newState));
    document.body.style.overflowY = newState ? 'hidden' : 'auto';
  };

  /* ───────────────────────────  RENDER  ─────────────────────────── */
  return (
    <>
      <Helmet>
        <title>{title && title !== '' ? title : 'TabanFood - Restaurant Platform'}</title>
        <link
          rel="icon"
          href={
            faviconIcon && faviconIcon !== ''
              ? getFileUrl(faviconIcon, 'theme')
              : '/favicon.ico'
          }
        />
      </Helmet>

      <header className="shadow-xs bg-white ff-header">
        <div className="container flex flex-col lg:flex-row items-center justify-between">
          {/* ── Left: Logo ────────────────────────────────────────────── */}
          <div className="w-full flex items-center justify-between gap-3 xl:gap-8 lg:justify-start lg:w-fit">
            <Link to={`/menu/table/${slug || ''}`} className="flex-shrink-0">
              <img
                className="w-28 sm:w-40"
                src={
                  generalSetting?.theme_logo && generalSetting?.theme_logo !== ''
                    ? getFileUrl(generalSetting?.theme_logo, 'theme')
                    : '/assets/images/theme/theme-logo.png'
                }
                alt="logo"
              />
            </Link>

            {/* Mobile cart button */}
            <div className="flex items-center gap-2 lg:hidden">
              <button
                type="button"
                onClick={handleDrawer}
                className="webcart flex items-center justify-center gap-1.5 w-fit rounded-3xl capitalize text-sm font-medium h-8 px-2.5 transition text-white bg-heading"
              >
                <i className="fa-solid fa-bag-shopping text-sm" />
                <span className="whitespace-nowrap">
                  <CurrencyCodeAdd price={tableOrder?.total || 0} />
                </span>
              </button>
            </div>
          </div>

          {/* ── Right: Auth buttons, credit badge, cart ──────────────── */}
          <div className="flex flex-col items-center justify-end gap-3 w-full mt-4 lg:flex-row lg:w-fit lg:mt-0">
            {isCustomerLoggedIn ? (
              <>
                {/* Dashboard button (desktop) */}
                <button
                  type="button"
                  onClick={() => navigate('/customer/dashboard')}
                  className="dashboard-btn hidden lg:flex items-center gap-1.5 rounded-3xl text-sm font-medium h-8 px-3 transition bg-blue-500 text-white"
                >
                  <i className="fa-solid fa-tachometer-alt text-sm" />
                  <span className="whitespace-nowrap">Dashboard</span>
                </button>

                {/* CREDIT BALANCE PILL (desktop) */}
                {creditEnabled && (
                  <span className="hidden lg:inline-flex items-center rounded-3xl bg-green-100 text-green-700 text-xs font-medium h-8 px-3">
                    Credit&nbsp;$&nbsp;{Number(creditBalance || 0).toFixed(2)}
                  </span>
                )}

                {/* Logout (desktop) */}
                <button
                  type="button"
                  onClick={handleLogout}
                  className="logout-btn hidden lg:flex items-center gap-1.5 rounded-3xl text-sm font-medium h-8 px-3 transition bg-red-500 text-white"
                >
                  <i className="fa-solid fa-sign-out-alt text-sm" />
                  <span className="whitespace-nowrap">Logout</span>
                </button>
              </>
            ) : (
              /* Not logged in → Login button (desktop) */
              <button
                type="button"
                onClick={() =>
                  navigate('/sign-in-customer', {
                    state: { from: getCurrentRoutePath() },
                  })
                }
                className="login-btn hidden lg:flex items-center gap-1.5 rounded-3xl text-sm font-medium h-8 px-3 transition bg-gray-200 text-heading"
              >
                <i className="fa-solid fa-user text-sm" />
                <span className="whitespace-nowrap">Login</span>
              </button>
            )}

            {/* Desktop cart button */}
            <button
              type="button"
              onClick={handleDrawer}
              className="webcart hidden lg:flex items-center gap-1.5 rounded-3xl capitalize text-sm font-medium h-8 px-3 transition text-white bg-heading"
            >
              <i className="fa-solid fa-bag-shopping text-sm" />
              <span className="whitespace-nowrap">
                <CurrencyCodeAdd price={tableOrder?.total || 0} />
              </span>
            </button>
          </div>
        </div>
      </header>
    </>
  );
};

export default MainHeader;
